import { JobConditions } from '@agent-ds/shared/components/job-condition-add-dialog/job-condition-add-dialog.component';
import {
  ALL_GRAD_CATEGORIES,
  GradCategory,
  SalaryTableItemPrefix,
  WAGE_CHECK_FIELD_LABELS,
  WAGE_CHECK_FIELDS,
  WAGE_PREFIX,
  WageCheckField,
} from '@agent-ds/shared/interfaces/job-az-check';
import { buildSalaryTableKey, getGradCategoryLabel } from '@agent-ds/shared/util/salary-table-key';

// 種別追加ダイアログの結果から対象の卒業種別一覧を決定する（未指定の場合は種別なし扱い）
export function resolveConditionGradCategories(conditions: JobConditions): GradCategory[] {
  if (conditions.gradCategories && conditions.gradCategories.length > 0) {
    return conditions.gradCategories as GradCategory[];
  }
  return ['noCategory'];
}

export function getWageCheckFieldLabel(field: string): string {
  return WAGE_CHECK_FIELD_LABELS[field as WageCheckField] || '';
}

/**
 * 賃金チェック結果（項目×卒業種別）を相談対象・相談内容の管理キーとして一意に識別するためのキーを生成する。
 * 通常項目のfieldKey（jobのdynamicDataフィールド名。'breakHour'等）と衝突しないようWAGE_PREFIXを付ける。
 * @param field 賃金チェック項目(WageCheckField)
 * @param gradCategory 卒業種別
 * @returns 接頭辞、接尾辞付きの一意なkey
 */
export function buildWageConsultationKey(field: string, gradCategory: GradCategory | null): string {
  return `${WAGE_PREFIX}${field}_${gradCategory || 'none'}`;
}

/**
 * WageCheckField を特定する(getWageCheckFieldLabel 等、生のfield名を必要とする箇所で使用する)。
 * @param fieldKey 賃金チェック項目(APIから返却された接頭辞、接尾辞付き)
 * @returns WageCheckField
 */
export function resolveWageCheckField(fieldKey: string): WageCheckField | undefined {
  return WAGE_CHECK_FIELDS.find((field) => fieldKey.startsWith(`${WAGE_PREFIX}${field}_`));
}

// 短縮反映用：卒業種別の短縮ラベルを取得する（大学院卒→院卒、大学卒→大卒。それ以外は通常ラベルのまま）
export function getShortenedGradCategoryLabel(category: GradCategory): string {
  switch (category) {
    case 'postGraduateGrad':
      return '院卒';
    case 'universityGrad':
      return '大卒';
    default:
      return getGradCategoryLabel(category);
  }
}

// 卒業種別の複数ラベルを結合する（末尾以外の「卒」を省略し「・」で繋ぐ。例: 大学院卒・大学卒 → 大学院・大学卒）
function formatGradCategoryLabels(labels: string[]): string {
  return labels.map((label, i) => (i < labels.length - 1 && label.endsWith('卒') ? label.slice(0, -1) : label)).join('・');
}

// 短縮反映用：金額の表示形式を取得する（短縮時は万円換算、通常時は円のカンマ区切り）
function formatSalaryAmountDisplay(amount: number, shorten: boolean): string {
  return shorten ? `${amount / 10000}万円` : `${amount.toLocaleString()}円`;
}

export interface SalaryTableOvertimeGroup {
  labels: string[];
  hours: number;
  amount: number;
}

export interface SalaryTableSalaryGroup {
  labels: string[];
  amount: number;
}

// 卒業種別ごとに指定したprefix群の値を取り出し、値が全て同じ種別同士でグルーピングする（種別なしのラベルは空文字のため一覧に含めない）
function groupGradCategoriesByValues(
  salaryTable: { [key: string]: any },
  prefixes: SalaryTableItemPrefix[],
  labelResolver: (category: GradCategory) => string,
): { labels: string[]; values: number[] }[] {
  const groups: { labels: string[]; values: number[] }[] = [];
  ALL_GRAD_CATEGORIES.forEach((category) => {
    const values = prefixes.map((prefix) => salaryTable[buildSalaryTableKey(prefix, category)]);
    if (values.some((value) => value == null)) {
      return;
    }
    const label = labelResolver(category);
    const existing = groups.find((group) => group.values.every((value, i) => value === values[i]));
    if (existing) {
      if (label) {
        existing.labels.push(label);
      }
      return;
    }
    groups.push({ labels: label ? [label] : [], values: values });
  });
  return groups;
}

export function groupGradCategoriesByHoursAndAmount(
  salaryTable: { [key: string]: any },
  hoursPrefix: SalaryTableItemPrefix,
  amountPrefix: SalaryTableItemPrefix,
  labelResolver: (category: GradCategory) => string = getGradCategoryLabel,
): SalaryTableOvertimeGroup[] {
  return groupGradCategoriesByValues(salaryTable, [hoursPrefix, amountPrefix], labelResolver).map((group) => ({
    labels: group.labels,
    hours: group.values[0],
    amount: group.values[1],
  }));
}

export function groupGradCategoriesByAmount(
  salaryTable: { [key: string]: any },
  amountPrefix: SalaryTableItemPrefix,
  labelResolver: (category: GradCategory) => string = getGradCategoryLabel,
): SalaryTableSalaryGroup[] {
  return groupGradCategoriesByValues(salaryTable, [amountPrefix], labelResolver).map((group) => ({
    labels: group.labels,
    amount: group.values[0],
  }));
}

// 固定残業代補足（fixedOvertimeDescriptionNonEditable）の反映文言を生成する
export function formatFixedOvertimeDescription(salaryTable: { [key: string]: any }, shorten: boolean): string {
  const groups = groupGradCategoriesByHoursAndAmount(
    salaryTable,
    'fixedOvertimeHours_',
    'fixedOvertimeAmount_',
    shorten ? getShortenedGradCategoryLabel : getGradCategoryLabel,
  );
  return groups
    .map((group) => {
      const hoursUnit = shorten ? 'h' : '時間';
      return `${formatGradCategoryLabels(group.labels)}${group.hours}${hoursUnit}分${formatSalaryAmountDisplay(group.amount, shorten)}`;
    })
    .join('／');
}

// 給与補足のうち固定深夜割増分（salaryDescriptionFixedLateNightOvertime）の反映文言を生成する
export function formatFixedLateNightOvertimeDescription(salaryTable: { [key: string]: any }, shorten: boolean = false): string {
  const groups = groupGradCategoriesByHoursAndAmount(
    salaryTable,
    'fixedLateNightOvertimeHours_',
    'fixedLateNightOvertimeAmount_',
    shorten ? getShortenedGradCategoryLabel : getGradCategoryLabel,
  );
  if (!groups.length) {
    return '';
  }
  const content = groups
    .map((group) => {
      const hoursUnit = shorten ? 'h' : '時間';
      return `${formatGradCategoryLabels(group.labels)}${group.hours}${hoursUnit}分${formatSalaryAmountDisplay(group.amount, shorten)}`;
    })
    .join('／');
  return `深夜割増分${content}を含みます。`;
}

// 給与補足のうち卒業種別別月給（salaryDescriptionSalaryByGraduateCategory）の反映文言を生成する
export function formatSalaryByGraduateCategoryDescription(salaryTable: { [key: string]: any }, shorten: boolean = false): string {
  const groups = groupGradCategoriesByAmount(salaryTable, 'salary_', shorten ? getShortenedGradCategoryLabel : getGradCategoryLabel);
  if (!groups.length) {
    return '';
  }
  const content = groups
    .map((group) => `${formatGradCategoryLabels(group.labels)}${formatSalaryAmountDisplay(group.amount, shorten)}`)
    .join('／');
  return `月給:${content}`;
}

// 給与補足のうち変形労働時間制分（salaryDescriptionVariableWorkingHoursSystem）の反映文言を生成する
export function formatVariableWorkingHoursSystemDescription(variableWorkingHoursSystem: string | null): string | null {
  return variableWorkingHoursSystem ? `${variableWorkingHoursSystem}の変形労働時間制です。` : null;
}

// 固定残業代（salaryTable.fixedOvertimeFee）を「無」に変更した際、既入力の固定残業時間・金額（全卒業種別分）と固定残業代補足をクリアする。
export function clearFixedOvertimeCategoryValues(salaryTable: { [key: string]: any }): void {
  if (!salaryTable) {
    return;
  }
  // 固定残業代が無の場合深夜割増手当分もクリアする
  clearFixedLateNightOvertimeCategoryValues(salaryTable);
  salaryTable.fixedLateNightOvertimeType = null;

  ALL_GRAD_CATEGORIES.forEach((category) => {
    salaryTable[buildSalaryTableKey('fixedOvertimeHours_', category)] = null;
    salaryTable[buildSalaryTableKey('fixedOvertimeAmount_', category)] = null;
  });
  salaryTable.fixedOvertimeDescriptionNonEditable = null;
}

// 固定の深夜割増手当（salaryTable.fixedLateNightOvertimeType）を「有→未選択」に変更した際、既入力の深夜割増時間・金額（全卒業種別分）と反映済みの給与補足文言をクリアする。
export function clearFixedLateNightOvertimeCategoryValues(salaryTable: { [key: string]: any }): void {
  if (!salaryTable) {
    return;
  }
  ALL_GRAD_CATEGORIES.forEach((category) => {
    salaryTable[buildSalaryTableKey('fixedLateNightOvertimeHours_', category)] = null;
    salaryTable[buildSalaryTableKey('fixedLateNightOvertimeAmount_', category)] = null;
  });
  salaryTable.salaryDescriptionFixedLateNightOvertime = null;
}

// みなし労働時間制の有無（dynamicData.workhourType）が「有（事業場外みなし労働時間制）」等、種類を問わず「有」から始まる値かどうかを判定する。
export function isDeemedWorkingHoursSystemSelected(workhourType: string | null): boolean {
  return !!workhourType && workhourType.startsWith('有');
}

// みなし労働時間制の有無が「有」から始まる場合、固定残業代は「無」となるため、
// 固定残業代を「無」に強制した上で、既入力の固定残業時間・金額（固定の深夜割増手当分を含む）と固定残業代補足をクリアする。
export function forceFixedOvertimeFeeNoneForDeemedWorkingHours(salaryTable: { [key: string]: any }): void {
  if (!salaryTable) {
    return;
  }
  salaryTable.fixedOvertimeFee = '無';
  clearFixedOvertimeCategoryValues(salaryTable);
}
